import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, Loader2, AlertCircle, ChevronDown } from 'lucide-react';
import { cn } from '../lib/cn';

// ── Types ─────────────────────────────────────────────────────────────────────

export type DiscordMember = {
  discordId: string;
  username: string;
  displayName: string;
  avatar: string | null;
};

export interface DiscordMemberPickerProps {
  /** Scope-specific search fn provided by the caller (clan / org / guild). */
  search: (q: string) => Promise<{ members: DiscordMember[] }>;
  /** Currently selected Discord ID, or '' for none. */
  value: string;
  /** Called when the user picks a member (member passed for caller caching) or clears (id ''). */
  onChange: (discordId: string, member?: DiscordMember) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Shown instead of the search input when disabled. */
  disabledHint?: string;
  /** When true (default) render a collapsible paste-an-ID fallback. */
  allowPasteId?: boolean;
  /** Optional display name for the current value — shown in the selected chip. */
  selectedLabel?: string;
}

const DISCORD_ID_RE = /^\d{17,20}$/;

// ── Small avatar helper ────────────────────────────────────────────────────────

function MemberAvatar({
  discordId,
  avatar,
  label,
}: {
  discordId: string;
  avatar: string | null;
  label: string;
}) {
  if (avatar) {
    return (
      <img
        src={`https://cdn.discordapp.com/avatars/${discordId}/${avatar}.png?size=32`}
        alt=""
        className="h-6 w-6 shrink-0 rounded-full"
      />
    );
  }
  return (
    <span className="flex h-6 w-6 shrink-0 items-center justify-center bg-iron-700 font-display text-[9px] font-black text-iron-200">
      {label.slice(0, 2).toUpperCase()}
    </span>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export function DiscordMemberPicker({
  search,
  value,
  onChange,
  placeholder = 'Type a name…',
  disabled = false,
  disabledHint,
  allowPasteId = true,
  selectedLabel,
}: DiscordMemberPickerProps) {
  // ── ALL hooks above any early return ──────────────────────────────────────
  const pickerId = useRef(Math.random().toString(36).slice(2)).current;
  const [query, setQuery] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteVal, setPasteVal] = useState('');
  const [pasteError, setPasteError] = useState('');
  // Cache the last member picked from the dropdown — used to show name/avatar in
  // the chip even when the caller only stores the ID (onChange={setState}).
  const [pickedMember, setPickedMember] = useState<DiscordMember | null>(null);

  // Sync: if value is cleared externally, forget the cached member.
  useEffect(() => {
    if (!value) setPickedMember(null);
  }, [value]);

  // Debounce search query 300ms; strip leading @.
  useEffect(() => {
    const t = setTimeout(
      () => setDebouncedQ(query.trim().replace(/^@+/, '')),
      300,
    );
    return () => clearTimeout(t);
  }, [query]);

  const searchResult = useQuery({
    queryKey: ['discordMemberSearch', pickerId, debouncedQ],
    queryFn: () => search(debouncedQ),
    // Only fetch when searching (not selected, not disabled, query long enough).
    enabled: !disabled && !value && debouncedQ.length >= 2,
    staleTime: 30_000,
  });

  // ── Selected chip ──────────────────────────────────────────────────────────
  if (value) {
    // pickedMember is fresh only if its ID still matches the current value.
    const chipMember = pickedMember?.discordId === value ? pickedMember : null;
    const displayLabel = selectedLabel ?? chipMember?.displayName ?? value;
    return (
      <div className="flex items-center gap-2 border border-forge-500/40 bg-iron-950 px-3 py-2">
        <MemberAvatar
          discordId={chipMember?.discordId ?? value}
          avatar={chipMember?.avatar ?? null}
          label={displayLabel}
        />
        <span className="flex-1 truncate text-sm text-white">{displayLabel}</span>
        <button
          type="button"
          onClick={() => { setPickedMember(null); onChange(''); }}
          className="text-iron-500 hover:text-iron-300"
          aria-label="Clear selection"
        >
          <X className="h-3.5 w-3.5" strokeWidth={2.5} />
        </button>
      </div>
    );
  }

  // ── Disabled state ─────────────────────────────────────────────────────────
  if (disabled) {
    return (
      <p className="text-[11px] italic text-iron-500">
        {disabledHint ?? 'Unavailable'}
      </p>
    );
  }

  // ── Searching / idle ───────────────────────────────────────────────────────
  const showDropdown = debouncedQ.length >= 2;
  const isEmpty =
    !searchResult.isFetching &&
    !searchResult.isError &&
    (searchResult.data?.members.length ?? 0) === 0;

  return (
    <div className="space-y-1.5">
      {/* Search input + live dropdown */}
      <div className="relative">
        <input
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder={placeholder}
          className="input w-full text-sm"
        />

        {showDropdown && (
          <div className="absolute z-10 mt-1 max-h-48 w-full overflow-y-auto border border-iron-700 bg-iron-950 shadow-forged">
            {searchResult.isFetching ? (
              <p className="flex items-center gap-2 px-3 py-2 text-xs text-iron-400">
                <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2.5} />
                Searching…
              </p>
            ) : searchResult.isError ? (
              <div className="px-3 py-2 text-xs">
                <div className="flex items-center gap-2 text-red-300">
                  <AlertCircle className="h-3.5 w-3.5 shrink-0" strokeWidth={2.5} />
                  {(searchResult.error as Error).message}
                </div>
                {allowPasteId && (
                  <p className="mt-1 text-iron-500">No matches — paste an ID below</p>
                )}
              </div>
            ) : isEmpty ? (
              <p className="px-3 py-2 text-xs text-iron-500">
                {allowPasteId
                  ? 'No matches — paste an ID below'
                  : 'No one by that name found.'}
              </p>
            ) : (
              searchResult.data!.members.map(m => (
                <button
                  key={m.discordId}
                  type="button"
                  onClick={() => {
                    setPickedMember(m);
                    onChange(m.discordId, m);
                    setQuery('');
                    setDebouncedQ('');
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-iron-950"
                >
                  <MemberAvatar discordId={m.discordId} avatar={m.avatar} label={m.displayName} />
                  <span className="flex-1 truncate text-sm text-iron-100">{m.displayName}</span>
                  <span className="font-display text-[9px] font-black uppercase tracking-wider text-forge-300">
                    Select
                  </span>
                </button>
              ))
            )}
          </div>
        )}
      </div>

      {/* Paste-an-ID fallback */}
      {allowPasteId && (
        <div>
          <button
            type="button"
            onClick={() => { setPasteOpen(o => !o); setPasteError(''); }}
            className="flex items-center gap-1 text-[10px] text-iron-500 hover:text-iron-300"
          >
            <ChevronDown
              className={cn('h-3 w-3 transition-transform', pasteOpen && 'rotate-180')}
              strokeWidth={2.5}
            />
            Paste an ID
          </button>

          {pasteOpen && (
            <div className="mt-1.5 flex gap-2">
              <input
                value={pasteVal}
                onChange={e => { setPasteVal(e.target.value); setPasteError(''); }}
                placeholder="17–20 digit Discord ID"
                className="input flex-1 font-mono text-xs"
                type="text"
              />
              <button
                type="button"
                onClick={() => {
                  const id = pasteVal.trim();
                  if (!DISCORD_ID_RE.test(id)) {
                    setPasteError('Must be a 17–20 digit numeric Discord ID');
                    return;
                  }
                  onChange(id);
                  setPasteVal('');
                  setPasteError('');
                  setPasteOpen(false);
                }}
                disabled={!pasteVal.trim()}
                className="inline-flex items-center rounded-md border border-iron-600 bg-iron-800 px-2 py-1 font-display text-[10px] font-black uppercase tracking-wider text-iron-200 hover:border-iron-500 hover:text-white disabled:opacity-40"
              >
                Use
              </button>
            </div>
          )}
          {pasteError && (
            <p className="mt-1 text-[10px] text-red-400">{pasteError}</p>
          )}
        </div>
      )}
    </div>
  );
}
